import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Popover from "@/components/ui/popover";
import { Actions as ActionsIcon } from "@/components/icons";
import type { LibraryFolderItem } from "../types/library.types";
import styles from "./FolderCard.module.scss";

export interface FolderCardProps {
  folder: LibraryFolderItem;
  onMenuAction?: (action: string, folder: LibraryFolderItem) => void;
  /** Klasör kartına (ikon + isim alanına) tıklanınca çağrılır – detay sayfasına gitmek için */
  onOpen?: (folder: LibraryFolderItem) => void;
  /** true ise sadece "Aç" gösterilir, Düzenle ve Sil gizlenir (client kütüphane) */
  readOnly?: boolean;
  /** true ise menüde "Sil" gösterilir (admin kütüphane + yetki) */
  canDelete?: boolean;
}

const FolderIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="72"
    height="72"
    viewBox="0 0 72 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M31.5 13.5H8.23544C4.92977 13.5 2.25 15.8113 2.25 19.125V29.25C2.25 25.9363 4.92977 23.25 8.23544 23.25L31.5 22.5L36 18L31.5 13.5Z"
      fill="url(#paint0_linear_83_270892)"
    />
    <path
      d="M2.25 28.5C2.25 25.1863 5.12817 22.5 8.67857 22.5H31.5L36 18H63.3214C66.8718 18 69.75 20.6863 69.75 24V57.0001C69.75 60.3138 66.8718 63.0001 63.3214 63.0001H8.67857C5.12817 63.0001 2.25 60.3138 2.25 57.0001L2.25 28.5Z"
      fill="url(#paint1_linear_83_270892)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_83_270892"
        x1="19.125"
        y1="11.25"
        x2="19.125"
        y2="29.25"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E6A200" />
        <stop offset="1" stopColor="#C68600" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_83_270892"
        x1="36"
        y1="15.75"
        x2="36"
        y2="63"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FFD766" />
        <stop offset="1" stopColor="#FEC52C" />
      </linearGradient>
    </defs>
  </svg>
);

const FolderCard: React.FC<FolderCardProps> = ({ folder, onMenuAction, onOpen, readOnly, canDelete }) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    // click (not mousedown) kullanıyoruz: menü portal'da render edildiği için
    // mousedown'da menü kapanıyor, buton unmount oluyor ve onClick hiç tetiklenmiyordu.
    if (menuOpen) document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [menuOpen]);

  const displayDate = folder.lastUpdatedFormatted;

  const menuContent = (
    <div className={styles.menuDropdown}>
      <button
        type="button"
        className={styles.menuItem}
        onClick={() => {
          setMenuOpen(false);
          onOpen?.(folder);
        }}
      >
        {t("library.folder.open")}
      </button>
      {!readOnly && canDelete && (
        <button
          type="button"
          className={styles.menuItem}
          onClick={() => {
            setMenuOpen(false);
            setTimeout(() => onMenuAction?.("delete", folder), 0);
          }}
        >
          {t("library.folder.delete")}
        </button>
      )}
    </div>
  );

  return (
    <div className={styles.card}>
      <div ref={menuRef} className={styles.topBar}>
        <Popover
          content={menuContent}
          isOpen={menuOpen}
          onClose={() => setMenuOpen(false)}
          position="bottom"
        >
          <button
            type="button"
            className={styles.menuButton}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            aria-label={t("common.more")}
          >
            <ActionsIcon width={16} height={16} />
          </button>
        </Popover>
      </div>
      <button
        type="button"
        className={styles.cardBody}
        onClick={() => onOpen?.(folder)}
        aria-label={t("library.folder.open")}
      >
        <div className={styles.folderIconContainer}>
          <FolderIcon className={styles.folderIcon} />
        </div>
        <div className={styles.folderNameContainer}>
          <h3 className={styles.folderName}>{folder.name}</h3>
          <div className={styles.lastUpdateRow}>
            <span className={styles.lastUpdateLabel}>
              {t("library.folder.lastUpdate")}
            </span>
            <span className={styles.lastUpdateDate}>{displayDate}</span>
          </div>
        </div>
      </button>
    </div>
  );
};

export default FolderCard;
